import React, { useEffect, useState } from 'react';
import BattleRunes from './BattleRunes';
import MonsterImage from '../components/MonsterImage';
import { getMonsterImgPath } from '../utils/monster';
import { guildImg } from '../utils/guildImage';

/**
 * **상대 이름으로 검색** — 전투기록 검색의 셋째 갈래. 이름 칸·후보 고르기까지만 여기서 한다.
 *
 * 상대 방덱 룬 보기가 켜진 사람에게만 보인다(사용자 지시 2026-10-10). 서버도 같은 권한으로 막는다
 * (`GET /api/siege-battles/opponent`). 이름으로 찾지만 묶는 것은 게임 아이디라, 개명해도 한 사람으로 모인다.
 *
 * 찾은 방덱들은 `onLoaded` 로 넘겨 **방덱·공덱 검색과 같은 결과 화면**(방덱 목록 → 공덱별 기록)에 띄운다
 * (사용자 지시: 스타일을 방덱·공덱 검색과 똑같이). 방덱마다 `counters` 가 `/search-counter` 응답과 같은 모양이다.
 */
interface Candidate { wizardId: number; name: string; guild: string; oldNames: string[]; battles: number; lastAt: string }
export interface OpponentWizard { wizardId: number; name: string; guild: string; oldNames: string[]; battles: number }
export interface OpponentDeck {
    key: string; team: any[]; source: string; counter_count: number; wins: number; losses: number; lastAt: string; counters: any[];
    /** 이 방덱의 룬이 기록된 가장 최근 전투 — 없으면 아직 리플레이를 못 받았다 */
    rune: { logId: number; ref?: string; battledAt: string; attacker: string } | null;
}

const ymd = (d: string) => {
    const t = new Date(d);
    return `${t.getFullYear()}.${String(t.getMonth() + 1).padStart(2, '0')}.${String(t.getDate()).padStart(2, '0')}`;
};
/** 날짜 + 시각 (`2026.10.08 21:34`) — 가장 최근 룬이 언제 전투 것인지 */
const ymdhm = (d: string) => {
    const t = new Date(d);
    return `${ymd(d)} ${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`;
};

export default function OpponentSearch({ isMobile, setLoading, onLoaded }: {
    isMobile: boolean;
    /** 방덱·공덱 검색과 같은 로딩 창을 띄운다 */
    setLoading: (on: boolean) => void;
    onLoaded: (wizard: OpponentWizard | null, decks: OpponentDeck[]) => void;
}) {
    const [name, setName] = useState('');
    const [err, setErr] = useState('');
    const [candidates, setCandidates] = useState<Candidate[] | null>(null);

    const loadWizard = async (id: number) => {
        setLoading(true); setErr('');
        try {
            const r = await fetch(`/api/siege-battles/opponent?wizardId=${id}`, { credentials: 'include' });
            const d = await r.json();
            if (!r.ok || !d.success) throw new Error(d.error || '불러오지 못했습니다');
            setCandidates(null);
            onLoaded(d.wizard, d.decks || []);
        } catch (e: any) {
            setErr(e.message || '불러오지 못했습니다');
        } finally {
            setLoading(false);
        }
    };

    const search = async () => {
        const q = name.trim();
        if (!q) return;
        setErr(''); setCandidates(null);
        onLoaded(null, []);
        setLoading(true);
        let list: Candidate[] = [];
        let failed = false;
        try {
            const r = await fetch(`/api/siege-battles/opponent?name=${encodeURIComponent(q)}`, { credentials: 'include' });
            const d = await r.json();
            if (!r.ok || !d.success) throw new Error(d.error || '찾지 못했습니다');
            list = d.candidates || [];
        } catch (e: any) {
            setErr(e.message || '찾지 못했습니다');
            failed = true;
        } finally {
            setLoading(false);
        }
        if (list.length === 1) return loadWizard(list[0].wizardId);
        setCandidates(list);
        if (!list.length && !failed) setErr('그 이름으로 우리가 공격한 기록이 없습니다');
    };

    const can = !!name.trim();

    // 이름 칸·버튼 — 몹 고르기 칸·[검색하기] 버튼과 같은 모양
    const inputStyle: React.CSSProperties = isMobile
        ? {
            width: '100%', boxSizing: 'border-box', padding: 'clamp(10px, 2vw, 12px) 14px', borderRadius: 'clamp(12px, 2.5vw, 14px)',
            border: '2px dashed #ddd', background: '#fff', fontSize: 'clamp(14px, 3vw, 16px)', fontWeight: 700, color: '#333',
            outline: 'none', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }
        : {
            width: 320, boxSizing: 'border-box', padding: '14px 18px', borderRadius: 12,
            border: '2px dashed #ddd', background: '#fff', fontSize: 16, fontWeight: 700, color: '#333',
            outline: 'none', textAlign: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        };
    const buttonStyle: React.CSSProperties = isMobile
        ? {
            background: can ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'linear-gradient(135deg, #9CA3AF 0%, #6B7280 100%)',
            color: '#fff', border: 'none', borderRadius: 'clamp(12px, 2.5vw, 14px)',
            padding: 'clamp(10px, 2vw, 12px) clamp(32px, 6.5vw, 36px)', fontWeight: 900, fontSize: 'clamp(14px, 3vw, 16px)',
            cursor: can ? 'pointer' : 'not-allowed', letterSpacing: -0.5, textShadow: '0 2px 4px rgba(0,0,0,0.3)',
            opacity: can ? 1 : 0.6,
            boxShadow: can ? '0 4px 16px rgba(102, 126, 234, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.2)'
        }
        : {
            background: can ? '#667eea' : '#ccc', color: '#fff', border: 'none', borderRadius: 12,
            padding: '14px 40px', fontWeight: 700, fontSize: 16, cursor: can ? 'pointer' : 'not-allowed',
            boxShadow: can ? '0 2px 8px rgba(102, 126, 234, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'
        };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: isMobile ? 'clamp(12px, 2.5vw, 16px)' : 16, marginBottom: isMobile ? 0 : 24, width: '100%' }}>
            <input
                value={name}
                onChange={(e) => { setName(e.target.value); setErr(''); }}
                onKeyDown={(e) => { if (e.key === 'Enter') search(); }}
                placeholder="상대 소환사 이름"
                style={inputStyle}
            />
            <button type="button" onClick={search} disabled={!can} style={buttonStyle}>검색하기</button>

            {err && <div style={{ color: '#999', fontSize: isMobile ? 13 : 14, fontWeight: 600, textAlign: 'center' }}>{err}</div>}

            {/* 같은 이름을 쓴(쓰던) 사람이 여럿이면 고른다 — 방덱 목록 카드와 같은 모양 */}
            {candidates && candidates.length > 1 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 480 }}>
                    <div style={{ fontSize: 13, color: '#888', fontWeight: 600, textAlign: 'center' }}>이 이름을 쓴(쓰던) 소환사가 여럿입니다</div>
                    {candidates.map((c) => (
                        <div
                            key={c.wizardId} onClick={() => loadWizard(c.wizardId)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 10, background: '#f7faff', borderRadius: 12,
                                boxShadow: '0 1px 4px #0001', padding: isMobile ? '12px 16px' : '14px 22px',
                                border: '2px solid #e3f0ff', cursor: 'pointer', boxSizing: 'border-box'
                            }}
                        >
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 15, fontWeight: 800, color: '#333' }}>{c.name}</div>
                                <div style={{ fontSize: 12, fontWeight: 800, color: '#d32f2f' }}>{c.guild}</div>
                                {c.oldNames.length > 0 && <div style={{ fontSize: 11, color: '#999' }}>예전 이름: {c.oldNames.join(', ')}</div>}
                            </div>
                            <div style={{ textAlign: 'center', minWidth: 60 }}>
                                <div style={{ fontWeight: 700, fontSize: 16, color: '#1976d2' }}>{c.battles}개</div>
                                <div style={{ fontSize: 11, color: '#999', fontWeight: 600 }}>{ymd(c.lastAt)}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

        </div>
    );
}

/**
 * **고른 상대 방덱 카드** — 결과 화면 오른쪽 위(방덱 검색의 "방덱" 줄 자리).
 *
 * 몹 셋을 이름과 함께 크게 보이고, 몹을 누르면 **가장 최근 룬**(이 방덱의 룬이
 * 기록된 가장 최근 전투의 방어 쪽)으로 스탯/룬 창을 띄운다(사용자 지시 2026-10-10). 창은 전투기록 창과 같은
 * `BattleRunes` 다 — 셈·그림이 화면마다 달라지면 안 된다.
 */
export function OpponentDeckPanel({ deck, monsterMapping, isMobile }: {
    deck: OpponentDeck;
    monsterMapping: any;
    isMobile: boolean;
}) {
    const [idx, setIdx] = useState(0);
    // 다른 방덱을 고르면 리더부터
    useEffect(() => { setIdx(0); }, [deck.key]);

    const size = isMobile ? 50 : 56;
    const picked = deck.team[idx];
    const pickedImg = getMonsterImgPath(monsterMapping, picked);

    return (
        <div style={{
            marginBottom: 12, padding: isMobile ? '12px 10px' : '14px 14px', borderRadius: 14,
            background: 'rgba(102, 126, 234, 0.06)', border: '1px solid rgba(102, 126, 234, 0.15)'
        }}>
            {/* 몹 셋 — 누르면 그 몹의 룬 */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: isMobile ? 14 : 20, marginBottom: 10 }}>
                {deck.team.map((m, i) => {
                    const { imgPath, displayName } = getMonsterImgPath(monsterMapping, m);
                    const on = i === idx;
                    return (
                        <div key={i} onClick={() => setIdx(i)} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: 'pointer', width: size + 16 }}>
                            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.5, lineHeight: 1, color: i === 0 ? '#667eea' : 'transparent' }}>LEADER</span>
                            <div style={{
                                width: size, height: size, borderRadius: 12, overflow: 'hidden', background: '#fff',
                                border: on ? '3px solid #667eea' : '2px solid #e3e8f7',
                                boxShadow: on ? '0 4px 12px rgba(102,126,234,0.35)' : '0 1px 4px rgba(0,0,0,0.08)',
                                transform: on ? 'scale(1.06)' : 'scale(1)', transition: 'all 0.2s ease',
                                display: 'flex', alignItems: 'center', justifyContent: 'center'
                            }}>
                                <MonsterImage imgPath={imgPath} alt={displayName} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                            <span style={{
                                fontSize: 11, fontWeight: on ? 800 : 600, color: on ? '#667eea' : '#444', maxWidth: size + 16,
                                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>{displayName}</span>
                        </div>
                    );
                })}
            </div>

            {/* 가장 최근 룬 */}
            {deck.rune ? (
                <>
                    <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textAlign: 'center', marginBottom: 6 }}>
                        가장 최근 룬 · {ymdhm(deck.rune.battledAt)}
                    </div>
                    <BattleRunes
                        key={`${deck.rune.logId}-${idx}`}
                        logId={deck.rune.ref || deck.rune.logId}
                        pick={{ side: 'defense', idx }}
                        attribute={picked?.attribute}
                        name={pickedImg.displayName}
                        img={pickedImg.imgPath ? `/mobs/v3/${pickedImg.imgPath.replace(/\\/g, '/')}` : null}
                        isMobile
                    />
                </>
            ) : (
                <div style={{ fontSize: 12, color: '#999', fontWeight: 600, textAlign: 'center', padding: '6px 0' }}>
                    이 방덱의 룬은 아직 기록되지 않았습니다
                </div>
            )}
        </div>
    );
}

/**
 * **찾은 소환사 띠** — 상대 이름 검색 결과의 방덱 목록·공덱 목록 맨 위에 그 사람의 길드 엠블럼·이름·길드를 보인다(사용자).
 * 목록을 내리다 보면 누구 덱을 보고 있는지 잊는다. 길드 엠블럼은 그룹 카드와 같은 `/images/guild/<길드>.png`, 없으면 물음표 그림.
 */
export function OpponentBadge({ wizard, isMobile }: { wizard: OpponentWizard; isMobile: boolean }) {
    const size = isMobile ? 30 : 38;
    return (
        <div style={{
            display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 10, marginBottom: isMobile ? 10 : 14,
            padding: isMobile ? '7px 10px' : '9px 12px', borderRadius: 12, textAlign: 'left',
            background: 'linear-gradient(135deg, #eef2ff 0%, #f5f3ff 100%)', border: '1px solid rgba(102,126,234,0.2)'
        }}>
            <img
                src={guildImg(wizard.guild)}
                alt={wizard.guild}
                title={wizard.guild}
                loading="lazy"
                style={{ width: size, height: size, borderRadius: size * 0.24, objectFit: 'cover', flexShrink: 0, border: '1px solid rgba(0,0,0,0.08)' }}
                onError={(e) => { e.currentTarget.src = '/images/unknown2.png'; }}
            />
            <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: isMobile ? 14 : 16, fontWeight: 800, color: '#1f2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{wizard.name}</div>
                <div style={{ fontSize: isMobile ? 11 : 12, fontWeight: 700, color: '#667eea', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{wizard.guild || '길드 모름'}</div>
            </div>
        </div>
    );
}
